import { computed, defineComponent, onBeforeUnmount, onMounted, ref, set, useContext, useCssModule, useRoute, useRouter, watch, } from '@nuxtjs/composition-api';
import { ADFOX_CODES_DESKTOP, ADFOX_CODES_MOBILE } from '../Adfox';
import { BANNERS_BY_INDEXES_MOBILE } from './';
import { SHOULD_DISABLE as SHOULD_ADFOX_DISABLE } from '~/plugins/adfox';
import { enableRouterForDOMLinks, parseLinksInString, getElementTop, sendAnalytics } from '~/utils';
export default defineComponent({
    name: 'ArticleGallery',
    props: {
        gallery: {
            required: true,
            type: Object,
        },
        withCaption: {
            default: true,
            type: Boolean,
        },
    },
    setup(props) {
        const context = useContext();
        const css = useCssModule();
        const route = useRoute();
        const router = useRouter();
        const env = context.env;
        const viewport = context.$viewport;
        /**
         * Banners for mobile gallery
         */
        if (viewport.isLessThan('tablet')) {
            const bannersByIndexes = BANNERS_BY_INDEXES_MOBILE;
            const indexes = Object.keys(bannersByIndexes);
            const lastIndex = Number(indexes[indexes.length - 1]);
            const distance = Number(indexes[1]) - Number(indexes[0]);
            const gallery = props.gallery.gallery.map((item, i, array) => {
                if (i === array.length - 1) {
                    return item;
                }
                if (i in bannersByIndexes) {
                    item.config = ADFOX_CODES_MOBILE[bannersByIndexes[i]];
                    item.isVisible = true;
                    return item;
                }
                if (i > lastIndex && (i + 1) % distance === 0) {
                    item.config = ADFOX_CODES_MOBILE[bannersByIndexes[lastIndex]];
                    item.isVisible = true;
                    return item;
                }
                return item;
            });
            set(props.gallery, 'gallery', gallery);
        }
        /**
         * Images offsets.
         */
        const imagesOffsets = ref([]);
        /**
         * Current image index.
         */
        const currentImageIndex = ref(0);
        /**
         * Current image id.
         */
        const currentImageId = ref('');
        /**
         * Current image path.
         */
        const currentPath = ref('');
        /**
         * Current image referrer.
         */
        const currentReferrer = ref('');
        /**
         * Images observer.
         */
        const resizeObserver = ref();
        /**
         * List of image elements
         */
        const imagesRef = ref([]);
        const rootEl = ref();
        const thumbsTop = ref(0);
        const lastScrollPosition = ref(0);
        const isTicking = ref(false);
        const shouldShowThumbs = computed(() => {
            return viewport.isGreaterThan('desktop');
        });
        function setCopyright(image) {
            let { author = null, source = null } = image;
            if (typeof author !== 'string') {
                author = '';
            }
            if (author) {
                author = author.split(' / ').join('<span>&middot;</span>');
            }
            if (typeof author !== 'string') {
                source = '';
            }
            if (source) {
                source = source.split(' / ').join('<span>&middot;</span>');
            }
            return [author, source].filter(Boolean).join('<span>&middot;</span>');
        }
        function shouldShowCaption(item) {
            return props.withCaption && (item.caption || item.author || item.text);
        }
        function isImageVertical(item) {
            const { height = 0, width = 0 } = item?.metadata || {};
            return height > width;
        }
        /**
         * Scrolls to image
         * @param id
         */
        function scrollTo(id) {
            let behavior = 'auto';
            let index = 0;
            for (let i = 0, l = imagesRef.value.length; i < l; i++) {
                if (imagesRef.value[i].dataset.imageKey === id) {
                    index = i;
                    break;
                }
            }
            if (currentImageIndex.value + 1 === index || currentImageIndex.value - 1 === index) {
                behavior = 'smooth';
            }
            const { top = 0 } = imagesOffsets.value[index] || {};
            window.scrollTo({ top, left: 0, behavior });
            window.setTimeout(() => {
                currentImageIndex.value = index;
            }, 500);
        }
        /**
         * Performs updates on scroll
         */
        function updateOnScroll() {
            isTicking.value = false;
            const galleryPath = `/${props.gallery.url_alias}`;
            const { scrollY } = window || {};
            const galleryTop = imagesOffsets.value[0]?.top || 0;
            const galleryBottom = imagesOffsets.value[imagesOffsets.value.length - 1]?.bottom || 0;
            if (scrollY < galleryTop || scrollY > galleryBottom) {
                currentPath.value = galleryPath;
            }
            for (let i = 0, l = imagesOffsets.value.length; i < l; i++) {
                const { top, bottom } = imagesOffsets.value[i];
                // Handles current image
                if (scrollY >= top && scrollY < bottom) {
                    const closest = imagesRef.value[i];
                    if (!closest) {
                        continue;
                    }
                    currentImageIndex.value = i;
                    currentImageId.value = closest?.dataset?.imageKey || '';
                    currentPath.value = `${galleryPath}?image=${currentImageId.value}`;
                    let previous = null;
                    if (scrollY < lastScrollPosition.value) {
                        previous = imagesRef.value[i + 1];
                    }
                    else {
                        previous = imagesRef.value[i - 1];
                    }
                    currentReferrer.value = galleryPath;
                    if (previous) {
                        currentReferrer.value = `${galleryPath}?image=${previous?.dataset?.imageKey}`;
                    }
                }
            }
            lastScrollPosition.value = scrollY;
        }
        /**
         * Handles "scroll" event
         */
        function handleScroll() {
            if (!isTicking.value) {
                window.requestAnimationFrame(updateOnScroll);
            }
            isTicking.value = true;
        }
        /**
         * Handles custom switch article event
         */
        function handleSwitchArticle(event) {
            if (event.detail.startsWith(`/${props.gallery.url_alias}`)) {
                return window.addEventListener('scroll', handleScroll);
            }
            return window.removeEventListener('scroll', handleScroll);
        }
        /**
         * Calculates images offsets and thumbs top
         */
        function calcOffsets() {
            window.requestAnimationFrame(() => {
                imagesOffsets.value = (imagesRef.value || []).map((el) => {
                    const headerHeight = 40;
                    return {
                        bottom: getElementTop(el) + el.offsetHeight - headerHeight,
                        top: getElementTop(el) - headerHeight,
                    };
                });
                thumbsTop.value = 56;
            });
        }
        function handleAdfoxComplete(item, { state }) {
            item.isVisible = !state.isDisabled;
        }
        onBeforeUnmount(() => {
            if ('image' in route.value.query) {
                delete route.value.query.image;
            }
            resizeObserver.value?.disconnect();
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('switchArticle', handleSwitchArticle);
            window.removeEventListener('completePageHeaderAdfox', calcOffsets);
        });
        onMounted(async () => {
            if (!rootEl.value) {
                return;
            }
            enableRouterForDOMLinks({
                target: rootEl.value,
                router,
            });
            window.addEventListener('scroll', handleScroll);
            window.addEventListener('switchArticle', handleSwitchArticle);
            calcOffsets();
            window.addEventListener('completePageHeaderAdfox', calcOffsets);
            resizeObserver.value = new ResizeObserver(() => calcOffsets());
            currentPath.value = `/${props.gallery.url_alias}`;
            if ('image' in route.value.query) {
                currentPath.value = `/${props.gallery.url_alias}?image=${route.value.query.image}`;
                window.requestAnimationFrame(() => {
                    scrollTo(route.value.query.image);
                });
            }
            watch(() => currentPath.value, (path) => {
                const referer = env.HOST + currentReferrer.value;
                const url = env.HOST + path;
                history.replaceState(null, '', path);
                sendAnalytics.call(context, {
                    path,
                    referer,
                    url,
                });
            });
        });
        return {
            ADFOX_CODES_DESKTOP,
            ADFOX_CODES_MOBILE,
            SHOULD_ADFOX_DISABLE,
            css,
            currentImageId,
            currentImageIndex,
            currentPath,
            currentReferrer,
            handleAdfoxComplete,
            imagesOffsets,
            imagesRef,
            isImageVertical,
            lastScrollPosition,
            parseLinksInString,
            rootEl,
            scrollTo,
            setCopyright,
            shouldShowCaption,
            shouldShowThumbs,
            thumbsTop,
        };
    },
});
